import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProduccionCabecera, RoturaFilter, PendingStatusFilter, SortKey } from '@/types'
import { sd } from '@/utils/planningFormatters'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ItemTypeFilter } from '@/components/common/ItemTypeFilter'
import { MultiFiltro, type OpcionMulti } from '@/components/common/MultiFiltro'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import type { ComponentStatus } from '@/types/productionSequence'
import { ESTADOS_MATERIAL } from '@/utils/estadoMaterial'
import { COMP_BADGE } from '@/components/production-sequence/sequenceFormat'
import { BarraFiltrosActivos, filtrosDe, descripcionDe, type FiltroActivo } from '@/components/common/BarraFiltrosActivos'
import type { ItemType } from '@/utils/itemType'

export function FilterPanel({
  dark,
  producciones,
  orden, articulo, componente, opcionesArticulo = [], opcionesComponente = [],
  materialSelec, estados, rutasSelec, empresaSelec, tipoArticulo, rotura, roturaDelComponente, pendingStatus, retrasadas, almacenTab, opMin, opMax, sort, sortDir, activeFilters,
  allRutas, allAlmacenes, allEmpresas, isEmpresaAll, maxOpAll, avp,
  setOrden, setArticulo, setComponente, toggleMaterial, setRotura, setRoturaDelComponente, setPendingStatus, setRetrasadas, setAlmacenTab, setOpMin, setOpMax, setSort, setSortDir, setTipoArticulo,
  toggleEstado, toggleRuta, toggleEmpresa, clearAll,
}: {
  dark: boolean
  producciones: ProduccionCabecera[]
  /** Nº de orden de producción. */
  orden: string[]
  /** Artículo que la orden fabrica. */
  articulo: string[]
  /** Componente que la orden consume. */
  componente: string[]
  /** Catálogos de los dos campos con dominio cerrado: código + descripción. */
  opcionesArticulo?: OpcionMulti[]
  opcionesComponente?: OpcionMulti[]
  /** Semáforo de material: qué estados se enseñan. Vacío = todos. */
  materialSelec: Set<ComponentStatus>
  estados: Set<number>
  rutasSelec: Set<string>
  empresaSelec: Set<string>
  tipoArticulo: Set<ItemType>
  rotura: RoturaFilter
  /** Matiz de «Con rotura»: que rompa EL componente buscado. Ver el bloque de
   *  rotura más abajo. */
  roturaDelComponente: boolean
  pendingStatus: PendingStatusFilter
  retrasadas: boolean
  almacenTab: string
  opMin: number
  opMax: number
  sort: SortKey
  sortDir: 'asc' | 'desc'
  activeFilters: number
  allRutas: string[]
  allAlmacenes: string[]
  allEmpresas: string[]
  isEmpresaAll: boolean
  maxOpAll: number
  avp: boolean
  setOrden: (v: string[]) => void
  setArticulo: (v: string[]) => void
  setComponente: (v: string[]) => void
  toggleMaterial: (e: ComponentStatus) => void
  setRotura: (v: RoturaFilter) => void
  setRoturaDelComponente: (v: boolean) => void
  setPendingStatus: (v: PendingStatusFilter) => void
  setRetrasadas: (v: boolean) => void
  setAlmacenTab: (v: string) => void
  setOpMin: (v: number) => void
  setOpMax: (v: number) => void
  setSort: (v: SortKey) => void
  setSortDir: (v: 'asc' | 'desc') => void
  setTipoArticulo: (v: Set<ItemType>) => void
  toggleEstado: (st: number) => void
  toggleRuta: (r: string) => void
  toggleEmpresa: (e: string) => void
  clearAll: () => void
}) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [sheetOpen, setSheetOpen] = useState(false)
  const opDrag = useRef<'min' | 'max' | null>(null)

  const cardBg     = dark ? 'bg-slate-900 border-white/8'    : 'bg-white border-slate-200'
  const mutedTxt   = dark ? 'text-slate-500'                 : 'text-slate-400'
  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const btnActive  = `${btnBase} bg-sky-500 border-sky-500 text-white`

  const sheetBg = dark ? 'bg-slate-900' : 'bg-white'
  const sheetBorder = dark ? 'border-white/10' : 'border-slate-200'

  const SORT_KEYS: [SortKey, string][] = [
    ['fecha',     t('planning.filter.sortDate')],
    ['unidades',  t('planning.filter.sortUnits')],
    ['cobertura', t('planning.filter.sortCoverage')],
  ]

  const ROTURA_KEYS: [RoturaFilter, string][] = [
    ['all', t('planning.filter.allBreaks')],
    ['con', t('planning.filter.withBreak')],
    ['sin', t('planning.filter.withoutBreak')],
  ]

  // El mismo rótulo que pinta `ItemTypeFilter`; aquí hace falta para el chip.
  const tipoLabel = (ty: ItemType) =>
    ty === 'Q' || ty === 'QARF' ? t('common.itemType.chemicals')
      : ty === 'X'              ? t('common.itemType.semi')
        :                         t('common.itemType.product')

  const PENDING_KEYS: [PendingStatusFilter, string][] = [
    ['all',      t('planning.filter.productionPendingStatusAll')],
    ['pending',  t('planning.filter.productionPendingStatusPending')],
    ['finished', t('planning.filter.productionPendingStatusFinished')],
  ]

  // Franja única de filtros activos: TODO lo que está filtrando cabe aquí, y se
  // quita de uno en uno sin tener que buscar el control que lo puso. Ahora los
  // términos SÍ llevan de qué campo salen: con tres buscadores, saber si la
  // lista se quedó corta por la orden o por el componente es justo lo que hay
  // que poder leer de un vistazo.
  const filtrosActivos: FiltroActivo[] = [
    ...filtrosDe(orden, setOrden,
      { campo: t('common.busqueda.orden'), prefijo: 'ord:' }),
    // El chip enseña el mismo rótulo que la opción del desplegable —el código
    // formateado— y la descripción va al tooltip: en el chip no cabe, y una
    // franja con «ARF12104932 CLUB DE NUIT WOMAN 105ML» se come la pantalla.
    ...filtrosDe(articulo, setArticulo, {
      campo: t('common.busqueda.articulo'), prefijo: 'art:',
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo),
    }),
    ...filtrosDe(componente, setComponente, {
      campo: t('common.busqueda.componente'), prefijo: 'cmp:',
      etiquetaDe: etiquetaCatalogoDe(opcionesComponente),
      tooltipDe: descripcionDe(opcionesComponente),
    }),
    ...[...materialSelec].map(e => ({
      clave: `mat-${e}`, campo: t('planning.consumo.estadoMaterial'),
      valor: t(`sequence.componentStatus.${e}`),
      quitar: () => toggleMaterial(e),
    })),
    ...[...empresaSelec].map(emp => ({
      clave: `emp-${emp}`, campo: t('planning.filter.empresa'), valor: emp.toUpperCase(),
      quitar: () => toggleEmpresa(emp),
    })),
    ...[...tipoArticulo].map(ty => ({
      clave: `tipo-${ty}`, campo: t('common.itemType.label'), valor: tipoLabel(ty),
      quitar: () => setTipoArticulo(new Set([...tipoArticulo].filter(x => x !== ty))),
    })),
    ...[...estados].map(st => ({
      clave: `est-${st}`, campo: t('planning.filter.estadoD365'), valor: t(`planning.status.${st}`),
      quitar: () => toggleEstado(st),
    })),
    ...[...rutasSelec].map(r => ({
      clave: `ruta-${r}`, campo: t('planning.filter.mainRoute'), valor: r, tooltip: r,
      quitar: () => toggleRuta(r),
    })),
    ...(rotura !== 'all' ? [{
      clave: 'rot', campo: t('planning.filter.stockBreak'),
      valor: rotura === 'con' && roturaDelComponente && componente.length > 0
        ? t('planning.filter.breakOfComponent')
        : ROTURA_KEYS.find(([v]) => v === rotura)?.[1] ?? rotura,
      quitar: () => { setRotura('all' as RoturaFilter); setRoturaDelComponente(false) },
    }] : []),
    ...(pendingStatus !== 'all' ? [{
      clave: 'pend', campo: t('planning.filter.productionPendingStatus'),
      valor: PENDING_KEYS.find(([v]) => v === pendingStatus)?.[1] ?? pendingStatus,
      quitar: () => setPendingStatus('all' as PendingStatusFilter),
    }] : []),
    ...(retrasadas ? [{
      clave: 'retr', valor: t('planning.filter.delayed'),
      quitar: () => setRetrasadas(false),
    }] : []),
    ...(almacenTab ? [{
      clave: 'alm', campo: t('planning.filter.warehouse'), valor: almacenTab,
      quitar: () => setAlmacenTab(''),
    }] : []),
    ...((opMin > 0 || opMax < 9999) ? [{
      clave: 'op', campo: t('planning.excel.workers'),
      valor: `${opMin} — ${opMax >= 9999 ? t('planning.filter.workersMax') : opMax}`,
      quitar: () => { setOpMin(0); setOpMax(9999) },
    }] : []),
  ]

  const franja = filtrosActivos.length > 0 && (
    <BarraFiltrosActivos dark={dark} filtros={filtrosActivos} onLimpiar={clearAll} />
  )

  // TRES buscadores, uno por concepto. Antes era uno solo que cubría a la vez el
  // nº de orden, el artículo fabricado y los componentes consumidos; como dentro
  // de un campo los términos van con OR, no había manera de pedir «esta orden Y
  // este componente» —que es justo lo que se quiere al comprobar si una
  // producción concreta lleva una materia prima concreta—.
  //
  // Artículo y componente llevan CATÁLOGO —código + descripción— porque su
  // dominio es cerrado y un código suelto no le dice nada a nadie. Conservan
  // además el texto libre: pegar una lista de códigos que no estén cargados en
  // el rango visible tiene que seguir funcionando.
  //
  // El nº de orden se queda sin catálogo: son cientos de códigos sin nada que
  // los describa, y una lista de códigos a secas no ayuda a elegir.
  //
  // El nombre del campo va en el placeholder y no en una etiqueta aparte, para
  // que los tres quepan en una fila en escritorio y apilados en móvil.
  const buscador = (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-2">
      <MultiFiltro
        dark={dark} permitirLibre className="flex-1 min-w-0"
        seleccion={orden} onSeleccion={setOrden}
        placeholder={t('common.busqueda.orden')}
        title={t('common.busqueda.pistaPegar')}
      />
      <MultiFiltro
        dark={dark} permitirLibre className="flex-1 min-w-0"
        opciones={opcionesArticulo}
        seleccion={articulo} onSeleccion={setArticulo}
        placeholder={t('common.busqueda.articulo')}
        title={t('common.busqueda.pistaPegar')}
      />
      <MultiFiltro
        dark={dark} permitirLibre className="flex-1 min-w-0"
        opciones={opcionesComponente}
        seleccion={componente} onSeleccion={setComponente}
        placeholder={t('common.busqueda.componente')}
        title={t('common.busqueda.pistaPegar')}
      />
    </div>
  )

  // Slider handlers (shared between desktop and mobile)
  const sliderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const p    = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const v    = Math.round(p * maxOpAll)
    const eMax = opMax >= 9999 ? maxOpAll : opMax
    opDrag.current = Math.abs(v - opMin) <= Math.abs(v - eMax) ? 'min' : 'max'
    e.currentTarget.setPointerCapture(e.pointerId)
    if (opDrag.current === 'min') setOpMin(Math.min(v, eMax - 1))
    else setOpMax(v >= maxOpAll ? 9999 : Math.max(v, opMin + 1))
  }
  const sliderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!opDrag.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const p    = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const v    = Math.round(p * maxOpAll)
    const eMax = opMax >= 9999 ? maxOpAll : opMax
    if (opDrag.current === 'min') setOpMin(Math.min(v, eMax - 1))
    else setOpMax(v >= maxOpAll ? 9999 : Math.max(v, opMin + 1))
  }

  // Filter rows 2-5 (shared between desktop expanded panel and mobile sheet)
  const sharedFilters = (
    <>
      {/* Empresa multi-select — solo visible para usuarios con Empresa.All */}
      {isEmpresaAll && (
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>
            {t('planning.filter.empresa')} {empresaSelec.size > 0 && <span className="text-sky-500">({empresaSelec.size})</span>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allEmpresas.map(emp => (
              <button
                key={emp}
                onClick={() => toggleEmpresa(emp)}
                className={`text-[11px] px-2.5 py-1 rounded-lg border transition-colors font-semibold uppercase cursor-pointer ${
                  empresaSelec.has(emp) ? btnActive : btnNeutral
                }`}
              >
                {emp}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tipo de artículo (prefijo Q/P/X) */}
      <div>
        <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>
          {t('common.itemType.label')} {tipoArticulo.size > 0 && <span className="text-sky-500">({tipoArticulo.size})</span>}
        </div>
        <div className="w-52">
          <ItemTypeFilter dark={dark} value={tipoArticulo} onChange={setTipoArticulo} avp={avp} />
        </div>
      </div>

      {/* Estado D365 */}
      <div>
        <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>{t('planning.filter.estadoD365')}</div>
        <div className="flex flex-wrap gap-1.5">
          {[0,1,2,3,4,5].map(st => {
            const def    = sd(st)
            const active = estados.has(st)
            return (
              <button key={st} onClick={() => toggleEstado(st)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition-colors cursor-pointer ${
                  active ? btnActive : dark ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-white/10' : `${def.badge} hover:brightness-95`
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : def.dot}`} />
                {t(`planning.status.${st}`)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Semáforo de material.
          Va ARRIBA del de rotura porque lo sustituye en la práctica: el binario
          sólo sabía «se puede producir o no», y entre medias hay dos casos que
          se arreglan de maneras muy distintas — mover material de otro almacén,
          o llamar al proveedor. El de rotura se queda porque hay enlaces y
          costumbres que lo usan. */}
      <div className="flex gap-6 flex-wrap">
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>
            {t('planning.consumo.estadoMaterial')}
            {materialSelec.size > 0 && <span className="text-sky-500"> ({materialSelec.size})</span>}
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {ESTADOS_MATERIAL.map(e => (
              <button
                key={e}
                onClick={() => toggleMaterial(e)}
                title={t(`sequence.componentStatus.${e}`)}
                className={`text-[11px] px-2 py-1 rounded-lg border font-semibold transition-colors cursor-pointer ${
                  materialSelec.has(e) ? COMP_BADGE[e] : btnNeutral
                }`}
              >
                {t(`sequence.componentStatus.${e}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Rutas + rotura + estado pendientes */}
      <div className="flex gap-6 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>
            {t('planning.filter.mainRoute')} {rutasSelec.size > 0 && <span className="text-sky-500">({rutasSelec.size})</span>}
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-[72px] overflow-y-auto">
            {allRutas.map(r => (
              <button key={r} onClick={() => toggleRuta(r)}
                className={`text-[11px] px-2 py-1 rounded-lg border transition-colors font-medium truncate max-w-[180px] cursor-pointer ${
                  rutasSelec.has(r) ? btnActive : btnNeutral
                }`}
                title={r}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>{t('planning.filter.stockBreak')}</div>
          <div className="flex gap-1">
            {ROTURA_KEYS.map(([v, lbl]) => (
              <button key={v} onClick={() => setRotura(v)}
                className={`${btnBase} ${rotura === v
                  ? v === 'con' ? 'bg-red-500 border-red-500 text-white'
                    : v === 'sin' ? 'bg-emerald-500 border-emerald-500 text-white'
                    : btnActive
                  : btnNeutral}`}
              >
                {lbl}
              </button>
            ))}
          </div>
          {/* El matiz sólo aparece cuando hay componente buscado: sin él las dos
              opciones dirían lo mismo, y un control que no cambia nada confunde
              más que ayuda. */}
          {rotura === 'con' && componente.length > 0 && (
            <div className="flex flex-col gap-0.5 mt-1.5">
              {([false, true] as const).map(v => (
                <label key={String(v)} className={`flex items-center gap-1.5 text-[11px] cursor-pointer ${
                  roturaDelComponente === v ? (dark ? 'text-slate-100' : 'text-slate-900') : mutedTxt
                }`}>
                  <input
                    type="radio"
                    className="accent-red-500"
                    checked={roturaDelComponente === v}
                    onChange={() => setRoturaDelComponente(v)}
                  />
                  {t(v ? 'planning.filter.breakOfComponent' : 'planning.filter.breakOfAny')}
                </label>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>{t('planning.filter.productionPendingStatus')}</div>
          <div className="flex gap-1">
            {PENDING_KEYS.map(([v, lbl]) => (
              <button key={v} onClick={() => setPendingStatus(v)}
                className={`${btnBase} ${pendingStatus === v
                  ? v === 'pending'  ? 'bg-amber-500 border-amber-500 text-white'
                    : v === 'finished' ? 'bg-emerald-500 border-emerald-500 text-white'
                    : btnActive
                  : btnNeutral}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Almacenes — solo visible para usuarios con Empresa.All */}
      {isEmpresaAll && allAlmacenes.length > 0 && (
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>
            {t('planning.filter.warehouse')} {almacenTab && <span className="text-sky-500">· {almacenTab}</span>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setAlmacenTab('')}
              className={`shrink-0 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer ${almacenTab === '' ? btnActive : btnNeutral}`}
            >
              {t('planning.filter.allWarehouses')} <span className={`tabular-nums ml-1 ${almacenTab === '' ? 'text-white/70' : mutedTxt}`}>{producciones.length}</span>
            </button>
            {allAlmacenes.map(alm => {
              const count = producciones.filter(p => p.inventlocationid === alm).length
              return (
                <button key={alm}
                  onClick={() => setAlmacenTab(almacenTab === alm ? '' : alm)}
                  className={`shrink-0 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer ${almacenTab === alm ? btnActive : btnNeutral}`}
                >
                  {alm} <span className={`tabular-nums ml-1 ${almacenTab === alm ? 'text-white/70' : mutedTxt}`}>{count}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Operarios slider */}
      {maxOpAll > 1 && (
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${mutedTxt}`}>
            {t('planning.filter.workers')}&nbsp;
            <span className={dark ? 'text-slate-200' : 'text-slate-700'}>
              {opMin} — {opMax >= 9999 ? t('planning.filter.workersMax') : opMax}
            </span>
            {(opMin > 0 || opMax < 9999) && <span className="ml-1.5 text-sky-500">{t('planning.filter.workersActive')}</span>}
          </div>
          <div
            className="relative h-5 flex items-center max-w-xs cursor-pointer select-none touch-none"
            onPointerDown={sliderPointerDown}
            onPointerMove={sliderPointerMove}
            onPointerUp={() => { opDrag.current = null }}
            onPointerCancel={() => { opDrag.current = null }}
          >
            <div className={`absolute w-full h-1.5 rounded-full ${dark ? 'bg-slate-700' : 'bg-slate-200'}`} />
            <div
              className="absolute h-1.5 bg-sky-500 rounded-full pointer-events-none"
              style={{
                left: `${(opMin / maxOpAll) * 100}%`,
                width: `${((Math.min(opMax >= 9999 ? maxOpAll : opMax, maxOpAll) - opMin) / maxOpAll) * 100}%`,
              }}
            />
            <div className="absolute w-3.5 h-3.5 rounded-full border-2 border-white bg-sky-500 shadow-sm pointer-events-none"
              style={{ left: `calc(${(opMin / maxOpAll) * 100}% - 7px)` }} />
            <div className="absolute w-3.5 h-3.5 rounded-full border-2 border-white bg-sky-500 shadow-sm pointer-events-none"
              style={{ left: `calc(${((opMax >= 9999 ? maxOpAll : opMax) / maxOpAll) * 100}% - 7px)` }} />
          </div>
          <div className={`flex justify-between text-[9px] mt-1 max-w-xs ${mutedTxt}`}>
            <span>0 op.</span>
            <span>{maxOpAll} op.</span>
          </div>
        </div>
      )}
    </>
  )

  // ── Mobile layout ─────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div className={`rounded-xl border flex flex-col gap-2 px-3 py-2 ${cardBg} shadow-sm`}>
        <div className="flex gap-2 items-center">
          <div className="flex-1 min-w-0">{buscador}</div>
          <button
            onClick={() => setSheetOpen(true)}
            className={`shrink-0 flex items-center gap-1.5 ${activeFilters > 0 ? btnActive : btnNeutral}`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 4h12M4 8h8M6 12h4" strokeLinecap="round" />
            </svg>
            {t('planning.filter.filters')}
            {activeFilters > 0 && (
              <span className="text-[10px] font-bold bg-white/20 px-1 rounded-full">{activeFilters}</span>
            )}
          </button>
        </div>
        {franja}
        </div>

        {sheetOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setSheetOpen(false)} />
            <div className={`fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t ${sheetBg} ${sheetBorder} max-h-[82vh] flex flex-col`}>
              {/* Sheet header */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${sheetBorder} shrink-0`}>
                <span className={`text-[13px] font-bold ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('planning.filter.filters')} {activeFilters > 0 && <span className="text-sky-500">({activeFilters} {t('planning.filter.filtersCount')})</span>}
                </span>
                <div className="flex items-center gap-2">
                  {activeFilters > 0 && (
                    <button onClick={clearAll} className={`text-[11px] font-medium ${dark ? 'text-red-400' : 'text-red-500'}`}>
                      {t('planning.filter.clear')}
                    </button>
                  )}
                  <button
                    onClick={() => setSheetOpen(false)}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-lg bg-sky-500 text-white`}
                  >
                    {t('planning.filter.apply')}
                  </button>
                </div>
              </div>

              {/* Sheet content */}
              <div className="overflow-y-auto px-4 py-3 flex flex-col gap-4">
                {/* Sort + retrasadas */}
                <div className="flex gap-3 flex-wrap">
                  <div>
                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>{t('planning.filter.sort')}</div>
                    <div className="flex gap-1 flex-wrap">
                      {SORT_KEYS.map(([k, lbl]) => (
                        <button key={k}
                          onClick={() => { if (sort === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setSortDir('asc') } }}
                          className={sort === k ? btnActive : btnNeutral}
                        >
                          {lbl}{sort === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${mutedTxt}`}>{t('planning.filter.quickAccess')}</div>
                    <button
                      onClick={() => setRetrasadas(!retrasadas)}
                      className={`${btnBase} ${retrasadas ? 'bg-amber-500 border-amber-500 text-white' : btnNeutral}`}
                    >
                      {t('planning.filter.delayed')}{retrasadas ? (
                        <svg className="w-3 h-3 text-white inline ml-1" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 1l10 10M11 1L1 11"/></svg>
                      ) : ''}
                    </button>
                  </div>
                </div>

                {sharedFilters}

                {/* Spacer */}
                <div className="h-2" />
              </div>
            </div>
          </>
        )}
      </>
    )
  }

  // ── Desktop layout ────────────────────────────────────────────────────────────
  // `overflow-hidden` recorta las esquinas redondeadas, pero la página lo monta
  // dentro de un flex column de altura fija (`h-full min-h-0`) donde este panel
  // es comprimible. Cuando el contenido crece —usuarios con Empresa.All ven las
  // secciones de empresa y almacén, y muchas más rutas— el navegador comprimía el
  // panel y el recorte se comía Almacén y Operarios sin dejar barra de scroll.
  // De ahí `flex flex-col` aquí y `min-h-0 overflow-y-auto` en el contenido: el
  // sobrante pasa a ser desplazable, igual que en el sheet móvil de más arriba.
  return (
    <div className={`rounded-xl border overflow-hidden flex flex-col ${cardBg} shadow-sm`}>
      <button
        onClick={() => setFiltersOpen(!filtersOpen)}
        className={`w-full shrink-0 flex items-center justify-between px-4 py-2.5 text-left transition-colors ${dark ? 'hover:bg-white/3' : 'hover:bg-slate-50'}`}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 ${mutedTxt}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 4h12M4 8h8M6 12h4" strokeLinecap="round" />
          </svg>
          <span className={`text-[11px] font-bold uppercase tracking-wider ${mutedTxt}`}>{t('planning.filter.title')}</span>
          {activeFilters > 0 && (
            <span className="text-[10px] font-bold bg-sky-500 text-white px-1.5 py-px rounded-full">{activeFilters}</span>
          )}
        </div>
        <span className={`text-[11px] ${mutedTxt}`}>{filtersOpen ? '▾' : '▸'}</span>
      </button>

      {filtersOpen && (
        <div className={`border-t px-4 py-3 flex flex-col gap-3 min-h-0 overflow-y-auto scroll-thin ${dark ? 'border-white/6' : 'border-slate-200'}`}>
          {/* Row 1: search + sort + retrasadas + clear */}
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${mutedTxt}`}>{t('planning.filter.search')}</div>
              {buscador}
            </div>
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${mutedTxt}`}>{t('planning.filter.sort')}</div>
              <div className="flex gap-1">
                {SORT_KEYS.map(([k, lbl]) => (
                  <button key={k}
                    onClick={() => { if (sort === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setSortDir('asc') } }}
                    className={sort === k ? btnActive : btnNeutral}
                  >
                    {lbl}{sort === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${mutedTxt}`}>{t('planning.filter.quickAccess')}</div>
              <button
                onClick={() => setRetrasadas(!retrasadas)}
                className={`${btnBase} ${retrasadas ? 'bg-amber-500 border-amber-500 text-white' : btnNeutral}`}
              >
                {t('planning.filter.delayed')}{retrasadas ? (
                  <svg className="w-3 h-3 text-white inline ml-1" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 1l10 10M11 1L1 11"/></svg>
                ) : ''}
              </button>
            </div>
            {activeFilters > 0 && (
              <button
                onClick={clearAll}
                className={`${btnBase} ${dark
                  ? 'border-red-800/50 bg-red-950/30 text-red-400 hover:bg-red-900/40'
                  : 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100'}`}
              >
                {t('planning.filter.clearCount', { count: activeFilters })}
              </button>
            )}
          </div>

          {sharedFilters}
        </div>
      )}

      {/* Fuera del plegable a propósito: si el usuario cierra el panel, tiene que
          seguir viendo qué está filtrando. */}
      {franja && (
        <div className={`px-4 py-2 border-t ${dark ? 'border-white/6' : 'border-slate-200'}`}>
          {franja}
        </div>
      )}
    </div>
  )
}
